import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from "@/contexts/FirebaseAuthContext";

const ProtectedRoute = ({ children }) => {
  const { user, profile, loading, authInitialized, refreshAuth } = useAuth();
  const [showManualCheck, setShowManualCheck] = useState(false);


  // Show manual check button if auth is initialized but no user
  useEffect(() => {
    if (!loading && authInitialized && !user) {
      console.log('ProtectedRoute: Auth initialized, no user, preparing for redirect to /login');
      // Give a small delay before showing the manual check button
      const timer = setTimeout(() => {
        setShowManualCheck(true);
      }, 2000); // Show manual check button after 2 seconds if still not logged in
      return () => clearTimeout(timer);
    } else {
      setShowManualCheck(false);
    }
  }, [loading, authInitialized, user]);

  // Add timeout for auth initialization to prevent infinite loading
  const [authTimeout, setAuthTimeout] = useState(false);
  
  useEffect(() => {
    if (loading || !authInitialized) {
      // Set a timeout to prevent infinite loading
      const timeout = setTimeout(() => {
        console.warn('⚠️ Auth initialization timeout - forcing state reset');
        setAuthTimeout(true);
      }, 8000); // 8 second timeout (reduced from 10)
      
      return () => clearTimeout(timeout);
    } else {
      setAuthTimeout(false);
    }
  }, [loading, authInitialized]);

  // Always wait for auth to be initialized before proceeding
  if (loading || !authInitialized) {
    if (authTimeout && !user) {
      return <Navigate to="/login" replace />;
    }
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-lg">Restoring your session...</p>
        <p className="text-gray-400 text-sm">Please wait while we log you back in</p>
      </div>
    );
  }
  
  // If auth timeout occurred and no user, redirect to login
  if (authTimeout && !user) {
    return <Navigate to="/login" replace />;
  }

  if (!user) {
    // If auth is initialized and no user, show a message and the manual login check
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white p-4">
        <p className="text-lg text-center mb-4">You need to be logged in to view this page.</p>
        {showManualCheck && (
          <div className="space-y-3">
            <button
              onClick={refreshAuth}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg shadow-lg transition-colors text-lg block w-full"
            >
              🔄 Check Login Status
            </button>
          </div>
        )}
        <Navigate to="/login" replace />
      </div>
    );
  }

  // Wait for profile to be loaded before rendering children to avoid hook ordering issues
  if (!profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-lg">Loading your profile...</p>
        <p className="text-gray-400 text-sm">Please wait</p>
      </div>
    );
  }

  // User is authenticated and profile is loaded - safe to render children
  return children;
};

const KaraokeProtectedRoute = ({ children }) => {
  const { user, profile, loading } = useAuth();
  const [isChecking, setIsChecking] = useState(false);

  // Add a small delay to prevent aggressive redirects during tab switching
  useEffect(() => {
    if (profile && profile.account_type === 'karaoke') {
      setIsChecking(false);
    } else if (profile && profile.account_type !== 'karaoke') {
      // Only redirect if we're certain the user is not karaoke
      const timer = setTimeout(() => {
        setIsChecking(true);
      }, 1000); // 1 second delay
      
      return () => clearTimeout(timer);
    }
  }, [profile]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-950 via-purple-900 to-indigo-950">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/karaoke/login" />;
  }

  // Wait for profile to be loaded before making redirect decisions
  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-950 via-purple-900 to-indigo-950">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  // Check if user is a karaoke mode user, but be less aggressive
  if (profile.account_type !== 'karaoke' && isChecking) {
    console.log('🚫 KaraokeProtectedRoute: User is not karaoke, redirecting to main dashboard');
    console.log('🔍 Profile data:', profile);
    // Redirect to main artist dashboard if they're not a karaoke user
    return <Navigate to="/admin" />;
  }

  console.log('✅ KaraokeProtectedRoute: User is karaoke, allowing access');
  return children;
};

export { ProtectedRoute, KaraokeProtectedRoute };